import { Button, Result, Space, Tag, Typography, theme as antdTheme } from 'antd';
import {
  CopyOutlined,
  HomeOutlined,
  ReloadOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import {
  isRouteErrorResponse,
  useNavigate,
  useRouteError,
} from 'react-router';

/**
 * Renders when a route subtree throws (during render, an effect, a loader,
 * or anything React Router can intercept). Wired as `errorElement` on the
 * `AppLayout` parent route in {@code router/index.tsx} so every authed page
 * inherits it without per-route opt-in.
 *
 * The user gets:
 *   - A clear "this page broke" message instead of a white screen
 *   - The trace id if the error carries one (server-thrown errors via our
 *     RFC-7807 handler include {@code traceId}; client-only errors don't)
 *   - Three actions: reload (drops the crashed subtree's state), back
 *     (returns to the previous route), and home (escape hatch)
 *   - A copy button for the traceId so they can quote it in a bug report
 *
 * The error message is shown verbatim only in development mode — in prod
 * we keep it terse to avoid leaking stack traces or sensitive paths.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { token } = antdTheme.useToken();

  const { title, detail, status, traceId } = classifyError(error);
  const isDev = import.meta.env.DEV;

  const copyTraceId = async () => {
    if (!traceId) return;
    try {
      await navigator.clipboard.writeText(traceId);
    } catch {
      // Older browsers / iframes — silently fail; the user can still
      // read it on screen.
    }
  };

  return (
    <div style={{ display: 'grid', placeItems: 'center', padding: 24 }}>
      <Result
        status={status === 404 ? '404' : 'error'}
        title={title}
        subTitle={
          <Space orientation="vertical" align="center" size={6} style={{ width: '100%' }}>
            <Typography.Text type="secondary">{detail}</Typography.Text>
            {traceId && (
              <Space size={6}>
                <Tag style={{ fontFamily: 'monospace', fontSize: 12 }}>
                  trace: {traceId}
                </Tag>
                <Button
                  size="small"
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={copyTraceId}
                  aria-label="Copy trace id"
                />
              </Space>
            )}
            {isDev && error instanceof Error && error.message && (
              <pre
                style={{
                  marginTop: 12,
                  padding: 12,
                  borderRadius: 8,
                  background: token.colorFillQuaternary,
                  color: token.colorTextSecondary,
                  fontSize: 11,
                  textAlign: 'left',
                  maxWidth: 560,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {error.message}
              </pre>
            )}
          </Space>
        }
        extra={
          <Space wrap>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => window.location.reload()}
            >
              Reload page
            </Button>
            <Button icon={<RollbackOutlined />} onClick={() => navigate(-1)}>
              Back
            </Button>
            <Button icon={<HomeOutlined />} onClick={() => navigate('/dashboard')}>
              Dashboard
            </Button>
          </Space>
        }
      />
    </div>
  );
}

interface ClassifiedError {
  title: string;
  detail: string;
  status?: number;
  traceId?: string;
}

/** Pull the most useful bits out of whatever React Router handed us. */
function classifyError(err: unknown): ClassifiedError {
  // 1. React Router's typed route errors (loader throws, 404, etc.).
  if (isRouteErrorResponse(err)) {
    if (err.status === 404) {
      return {
        title: 'Page not found',
        detail: "We couldn't find what you were looking for. The link may be stale.",
        status: 404,
      };
    }
    return {
      title: `Request failed · ${err.status}`,
      detail: typeof err.data === 'string'
        ? err.data
        : (err.statusText || 'Something went wrong on the request.'),
      status: err.status,
    };
  }

  // 2. RFC-7807 ProblemDetail flowing through axios (network errors, etc.).
  //    Our backend stamps `traceId` on every error body via GlobalExceptionHandler.
  if (typeof err === 'object' && err !== null) {
    const e = err as {
      response?: { status?: number; data?: { detail?: string; title?: string; traceId?: string } };
      message?: string;
    };
    const data = e.response?.data;
    if (data) {
      return {
        title: data.title ?? 'Something went wrong',
        detail: data.detail ?? e.message ?? 'Unexpected error',
        status: e.response?.status,
        traceId: data.traceId,
      };
    }
  }

  // 3. Plain Errors (render-time exceptions, missing variables, etc.).
  if (err instanceof Error) {
    return {
      title: 'Something went wrong',
      detail: 'The page hit an unexpected error. Try reloading — if it keeps happening, mention the trace id below to support.',
    };
  }

  return {
    title: 'Something went wrong',
    detail: 'Unexpected error while loading this page.',
  };
}
